/**
 * Solitaire (Klondike) Game Component
 * Classic single-player card game with drag-and-drop and click-to-move
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, RotateCcw, Undo2, Trophy, Clock, Layers, HelpCircle, X, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import useSolitaireStore, { SUITS } from './solitaireStore';

// Card Component
const Card = ({ card, onClick, onDoubleClick, isSelected, isDraggable, style, stackIndex = 0 }) => {
  const [isDragging, setIsDragging] = useState(false);
  
  if (!card) return null;
  
  const isRed = card.color === 'red';
  
  const handleDragStart = (e) => {
    if (!isDraggable || !card.faceUp) {
      e.preventDefault();
      return;
    }
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', JSON.stringify({ cardId: card.id }));
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragEnd = () => {
    setIsDragging(false);
  };
  
  if (!card.faceUp) {
    // Card back
    return (
      <div
        className={`w-[70px] h-[98px] rounded-lg cursor-pointer transition-all
          bg-gradient-to-br from-purple-700 to-purple-900 border-2 border-purple-500
          shadow-lg hover:shadow-purple-500/30 ${isDragging ? 'opacity-50' : ''}`}
        style={style}
        onClick={onClick}
        draggable={false}
      >
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-12 h-16 rounded border-2 border-purple-400/50 bg-purple-800/50 flex items-center justify-center">
            <span className="text-purple-300 text-2xl">♠</span>
          </div>
        </div>
      </div>
    );
  }
  
  // Card face
  return (
    <motion.div
      className={`w-[70px] h-[98px] rounded-lg cursor-pointer transition-all select-none
        bg-white border-2 shadow-lg
        ${isSelected ? 'border-cyan-400 ring-2 ring-cyan-400/50' : 'border-slate-300'}
        ${isDragging ? 'opacity-50 scale-105' : 'hover:shadow-xl'}
        ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={style}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      draggable={isDraggable && card.faceUp}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      initial={stackIndex === 0 ? { scale: 0.8, opacity: 0 } : false}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.2, delay: stackIndex * 0.02 }}
      data-testid={`card-${card.suit}-${card.rank}`}
    >
      <div className="w-full h-full p-1 flex flex-col">
        {/* Top left rank and suit */}
        <div className={`text-sm font-bold leading-none ${isRed ? 'text-red-600' : 'text-slate-900'}`}>
          {card.rank}
          <div className="text-xs">{card.symbol}</div>
        </div>
        
        {/* Center suit */}
        <div className={`flex-1 flex items-center justify-center text-3xl ${isRed ? 'text-red-600' : 'text-slate-900'}`}>
          {card.symbol}
        </div>
        
        {/* Bottom right rank and suit (inverted) */}
        <div className={`text-sm font-bold leading-none text-right rotate-180 ${isRed ? 'text-red-600' : 'text-slate-900'}`}>
          {card.rank}
          <div className="text-xs">{card.symbol}</div>
        </div>
      </div>
    </motion.div>
  );
};

// Pile placeholder (empty spot)
const EmptyPile = ({ type, onDrop, onDragOver, onClick, children, acceptsKing = false }) => {
  const [isOver, setIsOver] = useState(false);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsOver(true);
    if (onDragOver) onDragOver(e);
  };
  
  const handleDragLeave = () => {
    setIsOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    if (onDrop) onDrop(e);
  };
  
  return (
    <div
      className={`w-[70px] h-[98px] rounded-lg border-2 border-dashed transition-all
        ${type === 'foundation' ? 'border-emerald-500/50 bg-emerald-900/20' : 'border-slate-600/50 bg-slate-800/30'}
        ${isOver ? 'border-cyan-400 bg-cyan-900/30 scale-105' : ''}
        ${children ? '' : 'flex items-center justify-center'}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={onClick}
    >
      {children || (
        <span className={`text-2xl ${type === 'foundation' ? 'text-emerald-500/50' : 'text-slate-600/50'}`}>
          {type === 'foundation' ? 'A' : (acceptsKing ? 'K' : '')}
        </span>
      )}
    </div>
  );
};

// Stock pile (draw pile)
const StockPile = ({ cards, onDraw }) => {
  if (cards.length === 0) {
    return (
      <div
        className="w-[70px] h-[98px] rounded-lg border-2 border-dashed border-slate-600/50 
          bg-slate-800/30 flex items-center justify-center cursor-pointer
          hover:border-cyan-400 hover:bg-cyan-900/20 transition-all"
        onClick={onDraw}
        data-testid="stock-empty"
      >
        <RotateCcw className="w-6 h-6 text-slate-500" />
      </div>
    );
  }
  
  return (
    <div className="relative" onClick={onDraw} data-testid="stock-pile">
      {/* Show stack effect */}
      {cards.length > 2 && (
        <div className="absolute w-[70px] h-[98px] rounded-lg bg-purple-900 border-2 border-purple-600 top-0 left-0" 
          style={{ transform: 'translate(4px, 4px)' }} />
      )}
      {cards.length > 1 && (
        <div className="absolute w-[70px] h-[98px] rounded-lg bg-purple-800 border-2 border-purple-500 top-0 left-0"
          style={{ transform: 'translate(2px, 2px)' }} />
      )}
      <Card card={{ ...cards[cards.length - 1], faceUp: false }} />
    </div>
  );
};

// Waste pile (drawn cards)
const WastePile = ({ cards, drawCount, onCardClick, onCardDoubleClick, selectedCardId }) => {
  if (cards.length === 0) {
    return <EmptyPile type="waste" />;
  }
  
  // Show last 1 or 3 cards based on draw count
  const visibleCount = Math.min(drawCount, cards.length);
  const visibleCards = cards.slice(-visibleCount);
  
  return (
    <div className="relative w-[70px] h-[98px]" data-testid="waste-pile">
      {visibleCards.map((card, index) => (
        <div
          key={card.id}
          className="absolute top-0"
          style={{ left: index * 20 }}
        >
          <Card
            card={card}
            isSelected={selectedCardId === card.id}
            isDraggable={index === visibleCards.length - 1}
            onClick={() => index === visibleCards.length - 1 && onCardClick(card)}
            onDoubleClick={() => index === visibleCards.length - 1 && onCardDoubleClick(card)}
          />
        </div>
      ))}
    </div>
  );
};

// Foundation pile
const FoundationPile = ({ cards, index, onDrop, onCardClick, selectedCardId }) => {
  const handleDrop = (e) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    onDrop(data.cardId, index);
  };
  
  const topCard = cards.length > 0 ? cards[cards.length - 1] : null;
  
  if (!topCard) {
    return (
      <EmptyPile 
        type="foundation" 
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      />
    );
  }
  
  return (
    <div
      className="relative"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      data-testid={`foundation-${index}`}
    >
      <Card
        card={topCard}
        isSelected={selectedCardId === topCard.id}
        isDraggable={true}
        onClick={() => onCardClick(topCard, index)}
      />
      {cards.length > 1 && (
        <div className="absolute bottom-0 right-0 bg-slate-900/80 text-xs text-white px-1 rounded">
          {cards.length}
        </div>
      )}
    </div>
  );
};

// Tableau pile (main playing area)
const TableauPile = ({ cards, pileIndex, onDrop, onCardClick, onCardDoubleClick, selectedCardId, selectedPileIndex }) => {
  const handleDrop = (e) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    onDrop(data.cardId, pileIndex);
  };
  
  if (cards.length === 0) {
    return (
      <EmptyPile 
        type="tableau"
        acceptsKing={true}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => onCardClick(null, pileIndex)}
      />
    );
  }
  
  return (
    <div 
      className="relative"
      style={{ height: `${98 + (cards.length - 1) * 28}px` }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      data-testid={`tableau-${pileIndex}`}
    >
      {cards.map((card, cardIndex) => {
        const isSelected = selectedCardId === card.id || 
          (selectedPileIndex === pileIndex && selectedCardId && 
           cards.findIndex(c => c.id === selectedCardId) <= cardIndex);
        
        return (
          <div
            key={card.id}
            className="absolute left-0"
            style={{ top: cardIndex * 28, zIndex: cardIndex }}
          >
            <Card
              card={card}
              isSelected={isSelected}
              isDraggable={card.faceUp}
              stackIndex={cardIndex}
              onClick={() => onCardClick(card, pileIndex, cardIndex)}
              onDoubleClick={() => card.faceUp && onCardDoubleClick(card, pileIndex)}
            />
          </div>
        );
      })}
    </div>
  );
};

// Timer display
const Timer = ({ seconds }) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return (
    <span className="font-mono">
      {mins.toString().padStart(2, '0')}:{secs.toString().padStart(2, '0')}
    </span>
  );
};

// Win overlay
const WinOverlay = ({ score, time, moves, onNewGame }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
  >
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-gradient-to-br from-emerald-900 to-slate-900 border-2 border-emerald-500 rounded-2xl p-8 text-center shadow-2xl max-w-md"
    >
      <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
      <h2 className="text-3xl font-bold text-white mb-2">Congratulations!</h2>
      <p className="text-emerald-300 mb-6">You won the game!</p>
      
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="text-2xl font-bold text-white">{score}</div>
          <div className="text-xs text-slate-400">Score</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="text-2xl font-bold text-white"><Timer seconds={time} /></div>
          <div className="text-xs text-slate-400">Time</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="text-2xl font-bold text-white">{moves}</div>
          <div className="text-xs text-slate-400">Moves</div>
        </div>
      </div>
      
      <Button
        onClick={onNewGame}
        className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 font-bold"
      >
        Play Again
      </Button>
    </motion.div>
  </motion.div>
);

// Help modal
const HelpModal = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-slate-600 rounded-2xl p-6 max-w-lg max-h-[80vh] overflow-y-auto shadow-2xl"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">How to Play Solitaire</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="space-y-4 text-gray-300 text-sm">
        <div className="bg-slate-800/50 rounded-xl p-4">
          <h3 className="font-semibold text-amber-400 mb-2">Goal</h3>
          <p>Move all cards to the four foundation piles, building each suit from Ace to King.</p>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4">
          <h3 className="font-semibold text-cyan-400 mb-2">Tableau Rules</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>Build down in alternating colors (red on black, black on red)</li>
            <li>Only Kings can be placed on empty tableau spaces</li>
            <li>Click a face-down card to flip it</li>
            <li>Move multiple cards by dragging or clicking</li>
          </ul>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4">
          <h3 className="font-semibold text-emerald-400 mb-2">Foundation Rules</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>Build up by suit from Ace to King</li>
            <li>Double-click a card to auto-move to foundation</li>
          </ul>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4">
          <h3 className="font-semibold text-purple-400 mb-2">Stock Pile</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>Click to draw 1 or 3 cards (toggle in settings)</li>
            <li>When empty, click to recycle waste pile</li>
          </ul>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4">
          <h3 className="font-semibold text-rose-400 mb-2">Scoring</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>+10 points: Move card to foundation</li>
            <li>+5 points: Move card from waste to tableau</li>
            <li>+5 points: Reveal face-down card</li>
            <li>-15 points: Move card from foundation back</li>
          </ul>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

// Main Solitaire Component
const Solitaire = () => {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  
  const {
    stock,
    waste,
    foundations,
    tableau,
    gameStarted,
    gameWon,
    moves,
    score,
    time,
    drawCount,
    selectedCards,
    selectedSource,
    gamesPlayed,
    gamesWon,
    startGame,
    drawCards,
    selectCards,
    clearSelection,
    moveCards,
    autoMoveToFoundation,
    undo,
    tick,
    toggleDrawCount,
    history
  } = useSolitaireStore();
  
  // Timer
  useEffect(() => {
    const interval = setInterval(() => tick(), 1000);
    return () => clearInterval(interval);
  }, [tick]);
  
  // Auto-start game
  useEffect(() => {
    if (!gameStarted) {
      startGame(drawCount);
    }
  }, [gameStarted, startGame, drawCount]);
  
  // Handle card click from waste pile
  const handleWasteCardClick = useCallback((card) => {
    if (selectedCards && selectedSource) {
      // Already have selection - try to move or clear
      clearSelection();
    } else {
      selectCards([card], { type: 'waste' });
    }
  }, [selectedCards, selectedSource, selectCards, clearSelection]);
  
  // Handle waste card double-click (auto-move to foundation)
  const handleWasteDoubleClick = useCallback((card) => {
    autoMoveToFoundation(card, { type: 'waste' });
  }, [autoMoveToFoundation]);
  
  // Handle foundation card click
  const handleFoundationCardClick = useCallback((card, foundationIndex) => {
    if (selectedCards && selectedSource) {
      // Try to move selected cards here
      moveCards(selectedCards, selectedSource, { type: 'foundation', index: foundationIndex });
    } else {
      selectCards([card], { type: 'foundation', index: foundationIndex });
    }
  }, [selectedCards, selectedSource, selectCards, moveCards]);
  
  // Handle tableau card click
  const handleTableauCardClick = useCallback((card, pileIndex, cardIndex) => {
    const pile = tableau[pileIndex];
    
    if (!card) {
      // Empty pile clicked - try to move selected King
      if (selectedCards && selectedSource && selectedCards[0].rankValue === 13) {
        moveCards(selectedCards, selectedSource, { type: 'tableau', index: pileIndex });
      } else {
        clearSelection();
      }
      return;
    }
    
    if (!card.faceUp) {
      // Face-down card - no action
      return;
    }
    
    if (selectedCards && selectedSource) {
      // Try to move selected cards to this pile
      const success = moveCards(selectedCards, selectedSource, { type: 'tableau', index: pileIndex });
      if (!success) {
        // If move failed and clicked our own selection, deselect
        if (selectedSource.type === 'tableau' && selectedSource.index === pileIndex) {
          clearSelection();
        }
      }
    } else {
      // Select this card and all cards below it
      const cardsToSelect = pile.slice(cardIndex);
      selectCards(cardsToSelect, { type: 'tableau', index: pileIndex });
    }
  }, [tableau, selectedCards, selectedSource, selectCards, moveCards, clearSelection]);
  
  // Handle tableau card double-click (auto-move to foundation)
  const handleTableauDoubleClick = useCallback((card, pileIndex) => {
    autoMoveToFoundation(card, { type: 'tableau', index: pileIndex });
  }, [autoMoveToFoundation]);
  
  // Handle drag-drop to foundation
  const handleFoundationDrop = useCallback((cardId, foundationIndex) => {
    // Find the card and its source
    let card = null;
    let source = null;
    
    // Check waste
    const wasteCard = waste.find(c => c.id === cardId);
    if (wasteCard) {
      card = wasteCard;
      source = { type: 'waste' };
    }
    
    // Check tableau
    if (!card) {
      for (let i = 0; i < tableau.length; i++) {
        const idx = tableau[i].findIndex(c => c.id === cardId);
        if (idx !== -1) {
          card = tableau[i][idx];
          source = { type: 'tableau', index: i };
          break;
        }
      }
    }
    
    if (card && source) {
      moveCards([card], source, { type: 'foundation', index: foundationIndex });
    }
  }, [waste, tableau, moveCards]);
  
  // Handle drag-drop to tableau
  const handleTableauDrop = useCallback((cardId, pileIndex) => {
    // Find the card and its source
    let cards = [];
    let source = null;
    
    // Check waste
    const wasteCard = waste.find(c => c.id === cardId);
    if (wasteCard) {
      cards = [wasteCard];
      source = { type: 'waste' };
    }
    
    // Check foundations
    if (cards.length === 0) {
      for (let i = 0; i < foundations.length; i++) {
        if (foundations[i].length > 0 && foundations[i][foundations[i].length - 1].id === cardId) {
          cards = [foundations[i][foundations[i].length - 1]];
          source = { type: 'foundation', index: i };
          break;
        }
      }
    }
    
    // Check tableau
    if (cards.length === 0) {
      for (let i = 0; i < tableau.length; i++) {
        const idx = tableau[i].findIndex(c => c.id === cardId);
        if (idx !== -1) {
          cards = tableau[i].slice(idx);
          source = { type: 'tableau', index: i };
          break;
        }
      }
    }
    
    if (cards.length > 0 && source) {
      moveCards(cards, source, { type: 'tableau', index: pileIndex });
    }
  }, [waste, foundations, tableau, moveCards]);
  
  const selectedCardId = selectedCards?.[0]?.id;
  const selectedPileIndex = selectedSource?.type === 'tableau' ? selectedSource.index : null;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b-2 border-slate-700/50 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Solitaire</h1>
              <p className="text-xs text-slate-400">Klondike</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Stats */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <Trophy className="w-4 h-4 text-amber-400" />
                <span>{score}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Clock className="w-4 h-4 text-cyan-400" />
                <Timer seconds={time} />
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Layers className="w-4 h-4 text-purple-400" />
                <span>{moves}</span>
              </div>
            </div>
            
            {/* Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={undo}
                disabled={history.length === 0}
                className="text-slate-400 hover:text-white disabled:opacity-50"
                title="Undo (costs 5 points)"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleDrawCount}
                className={`${drawCount === 3 ? 'text-cyan-400' : 'text-slate-400'} hover:text-white`}
                title={`Draw ${drawCount === 1 ? '3' : '1'} cards`}
              >
                <span className="text-xs font-bold">{drawCount === 1 ? 'D1' : 'D3'}</span>
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHelp(true)}
                className="text-slate-400 hover:text-white"
              >
                <HelpCircle className="w-4 h-4" />
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => startGame(drawCount)}
                className="border-slate-600 text-white hover:bg-slate-800"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                New Game
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Game Area */}
      <div className="max-w-5xl mx-auto p-6">
        {/* Top row: Stock, Waste, Foundations */}
        <div className="flex justify-between mb-8">
          {/* Stock and Waste */}
          <div className="flex gap-4">
            <StockPile cards={stock} onDraw={drawCards} />
            <WastePile 
              cards={waste} 
              drawCount={drawCount}
              onCardClick={handleWasteCardClick}
              onCardDoubleClick={handleWasteDoubleClick}
              selectedCardId={selectedCardId}
            />
          </div>
          
          {/* Foundations */}
          <div className="flex gap-3">
            {foundations.map((foundation, index) => (
              <FoundationPile
                key={index}
                cards={foundation}
                index={index}
                onDrop={handleFoundationDrop}
                onCardClick={handleFoundationCardClick}
                selectedCardId={selectedCardId}
              />
            ))}
          </div>
        </div>
        
        {/* Tableau */}
        <div className="grid grid-cols-7 gap-4 justify-items-center px-4">
          {tableau.map((pile, index) => (
            <TableauPile
              key={index}
              cards={pile}
              pileIndex={index}
              onDrop={handleTableauDrop}
              onCardClick={handleTableauCardClick}
              onCardDoubleClick={handleTableauDoubleClick}
              selectedCardId={selectedCardId}
              selectedPileIndex={selectedPileIndex}
            />
          ))}
        </div>
        
        {/* Win stats */}
        {gamesPlayed > 0 && (
          <div className="mt-8 text-center text-slate-500 text-sm">
            Games: {gamesWon}/{gamesPlayed} won ({gamesPlayed > 0 ? Math.round(gamesWon / gamesPlayed * 100) : 0}%)
          </div>
        )}
      </div>
      
      {/* Win Overlay */}
      <AnimatePresence>
        {gameWon && (
          <WinOverlay
            score={score}
            time={time}
            moves={moves}
            onNewGame={() => startGame(drawCount)}
          />
        )}
      </AnimatePresence>
      
      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      </AnimatePresence>
    </div>
  );
};

export default Solitaire;
